/**
 * Customer Review Edit Page
 *
 * Allows customers to edit their submitted reviews within a 7-day window.
 * Pre-populates the form with existing review data.
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-12: Edit Review (2 SP)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/customer/reviews/:reviewId
 * - Updates via PUT /api/v1/customer/reviews/:reviewId
 *
 * Edit Window: 7 days after initial submission
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  AlertCircle as _AlertCircle,
  ArrowLeft,
  Calendar,
  MapPin as _MapPin,
  Clock,
  CheckCircle
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import ReviewEditRatingsStep from '../../components/reviews/ReviewEditRatingsStep';
import ReviewEditDetailsStep from '../../components/reviews/ReviewEditDetailsStep';
import ReviewEditPreviewStep from '../../components/reviews/ReviewEditPreviewStep';
import { logError } from '../../services/errorLoggingService';

export default function CustomerReviewEdit() {
  const navigate = useNavigate();
  const { reviewId } = useParams();

  const [review, setReview] = useState(null);
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState(null);

  // Review data state (matches wizard structure)
  const [reviewData, setReviewData] = useState({
    overall_rating: 0,
    communication_rating: 0,
    knowledge_rating: 0,
    professionalism_rating: 0,
    value_rating: 0,
    review_text: '',
    would_recommend: null,
    trip_highlights: '',
    areas_improvement: ''
  });

  useEffect(() => {
    const fetchReviewData = async () => {
      try {
        setLoading(true);
        const response = await apiRequest(`/api/v1/customer/reviews/${reviewId}`, { method: 'GET' });

        if (response.success) {
          const reviewData = response.data;
          setReview(reviewData);
          setTrip(reviewData.trip);

          // Check if review can still be edited
          const reviewTime = new Date(reviewData.created_at);
          const now = new Date();
          const daysSinceReview = Math.floor((now - reviewTime) / (1000 * 60 * 60 * 24));

          if (daysSinceReview > 7) {
            setError('This review can no longer be edited. The 7-day edit window has expired.');
            return;
          }

          // Populate form with existing data
          setReviewData({
            overall_rating: reviewData.overall_rating || 0,
            communication_rating: reviewData.category_ratings?.communication || 0,
            knowledge_rating: reviewData.category_ratings?.knowledge || 0,
            professionalism_rating: reviewData.category_ratings?.professionalism || 0,
            value_rating: reviewData.category_ratings?.value || 0,
            review_text: reviewData.review_text || '',
            would_recommend: reviewData.would_recommend ?? null,
            trip_highlights: reviewData.trip_highlights || '',
            areas_improvement: reviewData.areas_improvement || ''
          });
        }
      } catch (err) {
        logError(err, { componentName: 'CustomerReviewEdit', action: 'fetch_review' });
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.message || 'Failed to load review');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReviewData();
  }, [reviewId]);

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      setErrorMessage(null);
      const response = await apiRequest(`/api/v1/customer/reviews/${reviewId}`, {
        method: 'PUT',
        body: JSON.stringify(reviewData)
      });

      if (response.success) {
        navigate('/customer/reviews');
      }
    } catch (err) {
      logError(err, { componentName: 'CustomerReviewEdit', action: 'update_review' });
      if (err.status === 401) {
        setErrorMessage('Session expired. Please log in again.');
      } else {
        setErrorMessage(err.message || 'Failed to update review');
      }
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeField = (field, value) => {
    setReviewData({ ...reviewData, [field]: value });
  };

  // Validation functions
  const canProceedToStep2 = () => {
    return reviewData.overall_rating > 0 &&
           reviewData.communication_rating > 0 &&
           reviewData.knowledge_rating > 0 &&
           reviewData.professionalism_rating > 0 &&
           reviewData.value_rating > 0;
  };

  const canProceedToStep3 = () => {
    return reviewData.review_text.trim().length >= 50 &&
           reviewData.would_recommend !== null;
  };

  const canSave = () => {
    return canProceedToStep2() && canProceedToStep3();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout userRole="customer" activeNavId="reviews" onNavigate={handleNavigate}>
        <LoadingSpinner message="Loading review..." className="min-h-screen" />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="customer" activeNavId="reviews" onNavigate={handleNavigate}>
        <div className="space-y-6">
          <button
            onClick={() => navigate('/customer/reviews')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Reviews</span>
          </button>

          <ErrorAlert message={error} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="customer" activeNavId="reviews" onNavigate={handleNavigate}>
      <div className="space-y-6">
        {/* Error Messages */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
            <_AlertCircle className="w-5 h-5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/customer/reviews')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-2"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Reviews</span>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Edit Review</h1>
            <p className="mt-1 text-sm text-gray-500">
              Make changes to your review within the 7-day edit window
            </p>
          </div>
        </div>

        {/* Edit Window Warning */}
        {review && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-orange-800">Edit Window Active</p>
                <p className="text-sm text-orange-700 mt-1">
                  You can edit this review until {formatDate(new Date(new Date(review.created_at).getTime() + 7 * 24 * 60 * 60 * 1000))}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Trip Summary */}
        {trip && (
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Trip Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Experience</p>
                <p className="font-medium text-gray-900">{trip.experience_title}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Guide</p>
                <p className="font-medium text-gray-900">{trip.guide_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-medium text-gray-900 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(trip.start_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-medium text-gray-900 flex items-center">
                  <_MapPin className="w-4 h-4 mr-1" />
                  {trip.location}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Progress Indicator */}
        <Card>
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex-1">
                <div className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                      currentStep === step
                        ? 'bg-blue-600 text-white'
                        : currentStep > step
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                  </div>
                  {step < 3 && (
                    <div
                      className={`flex-1 h-1 mx-2 transition-colors ${
                        currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
                <p className={`text-xs mt-2 ${currentStep >= step ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  {step === 1 && 'Ratings'}
                  {step === 2 && 'Written Review'}
                  {step === 3 && 'Preview'}
                </p>
              </div>
            ))}
          </div>

          {/* Step 1: Ratings */}
          {currentStep === 1 && (
            <ReviewEditRatingsStep
              reviewData={reviewData}
              onChangeRating={handleChangeField}
              canProceed={canProceedToStep2()}
              onNext={() => setCurrentStep(2)}
            />
          )}

          {/* Step 2: Written Review */}
          {currentStep === 2 && (
            <ReviewEditDetailsStep
              reviewData={reviewData}
              onChangeField={handleChangeField}
              canProceed={canProceedToStep3()}
              onBack={() => setCurrentStep(1)}
              onNext={() => setCurrentStep(3)}
            />
          )}

          {/* Step 3: Preview */}
          {currentStep === 3 && (
            <ReviewEditPreviewStep
              reviewData={reviewData}
              onSave={handleSaveChanges}
              canSave={canSave()}
              saving={saving}
              onBack={() => setCurrentStep(2)}
            />
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
